package org.zq.linkgame.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.material3.MaterialTheme
import org.zq.linkgame.ui.components.*
import org.zq.linkgame.ui.model.AppLanguage
import org.zq.linkgame.ui.model.BackgroundTheme
import org.zq.linkgame.ui.model.LocalStrings
import org.zq.linkgame.ui.theme.*

/**
 * Main screen of the Link Game app
 * Contains: App logo, title, main buttons, and bottom action buttons
 */
@Composable
fun MainScreen(
    onStartGame: () -> Unit,
    onLevelSelect: () -> Unit,
    onLeaderboard: () -> Unit,
    onAboutUs: () -> Unit,
    backgroundTheme: BackgroundTheme,
    onBackgroundThemeChange: (BackgroundTheme) -> Unit,
    currentLanguage: AppLanguage,
    onLanguageChange: (AppLanguage) -> Unit,
    modifier: Modifier = Modifier
) {
    var showSettingsDialog by remember { mutableStateOf(false) }
    var showHowToPlayDialog by remember { mutableStateOf(false) }
    var showAuthDialog by remember { mutableStateOf(false) }
    var loggedInUser by remember { mutableStateOf<String?>(null) }
    val strings = LocalStrings.current
    
    BoxWithConstraints(
        modifier = modifier
            .fillMaxSize()
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(
                        Color(backgroundTheme.topColor),
                        Color(backgroundTheme.bottomColor)
                    )
                )
            )
    ) {
        val screenWidth = maxWidth
        val isLandscape = maxWidth > maxHeight
        
        if (isLandscape) {
            // Landscape layout: horizontal
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Left: Logo and title
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    AppLogoIcon(size = 80.dp)
                    Spacer(modifier = Modifier.height(8.dp))
                    AppText(
                        text = strings.mainTitle,
                        style = MaterialTheme.typography.headlineMedium,
                        color = Color(backgroundTheme.textColor),
                      
                    )
                }
                
                // Center: Main buttons
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    PrimaryGameButton(
                        text = strings.startGame,
                        onClick = onStartGame,
                        modifier = Modifier.width(200.dp)
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    SecondaryGameButton(
                        text = strings.levelSelect,
                        onClick = onLevelSelect,
                        modifier = Modifier.width(180.dp)
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    TertiaryGameButton(
                        text = strings.leaderboard,
                        onClick = onLeaderboard,
                        modifier = Modifier.width(160.dp)
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    TertiaryGameButton(
                        text = strings.howToPlay,
                        onClick = { showHowToPlayDialog = true },
                        modifier = Modifier.width(160.dp)
                    )
                }
                
                // Right: Login avatar and action buttons
                Column(
                    modifier = Modifier
                        .width(80.dp)
                        .fillMaxHeight(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.SpaceBetween
                ) {
                    CircleIconButton(
                        onClick = { showAuthDialog = true },
                        size = 50.dp,
                        backgroundColor = LightBlue
                    ) {
                        UserAvatarIcon(size = 26.dp, color = Color(backgroundTheme.textColor))
                    }
                    
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        BottomActionButton(
                            text = strings.settings,
                            onClick = { showSettingsDialog = true },
                            isPrimary = true
                        )
                        BottomActionButton(
                            text = strings.aboutUs,
                            onClick = onAboutUs,
                            isPrimary = false
                        )
                    }
                }
            }
        } else {
            // Portrait layout: vertical
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                TopArea(
                    onLogin = { showAuthDialog = true },
                    backgroundTheme = backgroundTheme,
                    modifier = Modifier.fillMaxWidth()
                )
                
                Spacer(modifier = Modifier.height(40.dp))
                
                MainButtonsArea(
                    screenWidth = screenWidth,
                    onStartGame = onStartGame,
                    onLevelSelect = onLevelSelect,
                    onLeaderboard = onLeaderboard,
                    onHowToPlay = { showHowToPlayDialog = true },
                    modifier = Modifier.weight(1f)
                )
                
                BottomArea(
                    onSettings = { showSettingsDialog = true },
                    onAboutUs = onAboutUs,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
    
    // Settings Dialog
    if (showSettingsDialog) {
        SettingsDialog(
            currentTheme = backgroundTheme,
            onThemeSelected = { theme ->
                onBackgroundThemeChange(theme)
            },
            currentLanguage = currentLanguage,
            onLanguageSelected = { language ->
                onLanguageChange(language)
            },
            onDismiss = { showSettingsDialog = false }
        )
    }
    
    // How to Play Dialog
    if (showHowToPlayDialog) {
        HowToPlayDialog(
            onDismiss = { showHowToPlayDialog = false }
        )
    }
    
    // Auth Dialog (Login/Register)
    if (showAuthDialog) {
        AuthDialog(
            onDismiss = { showAuthDialog = false },
            onLoginSuccess = { username ->
                loggedInUser = username
                showAuthDialog = false
            },
            onRegisterSuccess = { username ->
                loggedInUser = username
                showAuthDialog = false
            }
        )
    }
}

/**
 * Settings dialog for background theme and language selection
 */
@Composable
private fun SettingsDialog(
    currentTheme: BackgroundTheme,
    onThemeSelected: (BackgroundTheme) -> Unit,
    currentLanguage: AppLanguage,
    onLanguageSelected: (AppLanguage) -> Unit,
    onDismiss: () -> Unit
) {
    var showLanguageSelector by remember { mutableStateOf(false) }
    val strings = LocalStrings.current
    
    Dialog(onDismissRequest = onDismiss) {
        BoxWithConstraints {
            val isLandscape = maxWidth > maxHeight
            val dialogWidth = if (isLandscape) maxWidth * 0.7f else maxWidth * 0.9f
            val themeGridHeight = if (isLandscape) maxHeight * 0.35f else maxHeight * 0.35f
            val langGridHeight = if (isLandscape) maxHeight * 0.35f else maxHeight * 0.4f
            val themeColumns = if (isLandscape) 7 else 4
            val langColumns = if (isLandscape) 4 else 2
            
            Column(
                modifier = Modifier
                    .width(dialogWidth)
                    .clip(RoundedCornerShape(16.dp))
                    .background(Color.White)
                    .padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                AppText(
                    text = strings.settings,
                    style = MaterialTheme.typography.titleLarge,
                    color = DeepBlue
                )
                
                Spacer(modifier = Modifier.height(12.dp))
                
                // Language Section
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    AppText(
                        text = "${strings.language} / Language",
                        style = MaterialTheme.typography.titleSmall,
                        color = DarkGray
                    )
                    Row(
                        modifier = Modifier
                            .clip(RoundedCornerShape(8.dp))
                            .background(LightBlue)
                            .clickable { showLanguageSelector = !showLanguageSelector }
                            .padding(horizontal = 12.dp, vertical = 6.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        AppText(
                            text = currentLanguage.nativeName,
                            style = MaterialTheme.typography.bodyMedium,
                            color = DeepBlue,
                            maxLines = 1
                        )
                        Spacer(modifier = Modifier.width(4.dp))
                        AppText(
                            text = if (showLanguageSelector) "▲" else "▼",
                            style = MaterialTheme.typography.bodySmall,
                            color = DeepBlue
                        )
                    }
                }
                
                // Language Grid (expandable)
                if (showLanguageSelector) {
                    Spacer(modifier = Modifier.height(8.dp))
                    LazyVerticalGrid(
                        columns = GridCells.Fixed(langColumns),
                        modifier = Modifier
                            .height(langGridHeight)
                            .fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        verticalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        items(AppLanguage.entries.size) { index ->
                            val language = AppLanguage.entries[index]
                            LanguageItem(
                                language = language,
                                isSelected = language == currentLanguage,
                                onClick = {
                                    onLanguageSelected(language)
                                    showLanguageSelector = false
                                }
                            )
                        }
                    }
                }
                
                // Background theme section (hidden when language selector is open)
                if (!showLanguageSelector) {
                    Spacer(modifier = Modifier.height(12.dp))
                    
                    AppText(
                        text = strings.backgroundTheme,
                        style = MaterialTheme.typography.titleSmall,
                        color = DarkGray
                    )
                    
                    Spacer(modifier = Modifier.height(8.dp))
                    
                    // Scrollable theme grid
                    LazyVerticalGrid(
                        columns = GridCells.Fixed(themeColumns),
                        modifier = Modifier
                            .height(themeGridHeight)
                            .fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                        verticalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        items(BackgroundTheme.entries.size) { index ->
                            val theme = BackgroundTheme.entries[index]
                            ThemeColorItem(
                                theme = theme,
                                isSelected = theme == currentTheme,
                                onClick = { onThemeSelected(theme) }
                            )
                        }
                    }
                }
                
                Spacer(modifier = Modifier.height(12.dp))
                
                SecondaryGameButton(
                    text = strings.close,
                    onClick = onDismiss,
                    modifier = Modifier.width(100.dp)
                )
            }
        }
    }
}

/**
 * Language selection item
 */
@Composable
private fun LanguageItem(
    language: AppLanguage,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(6.dp))
            .background(if (isSelected) DeepBlue.copy(alpha = 0.1f) else Color.Transparent)
            .border(
                width = if (isSelected) 2.dp else 1.dp,
                color = if (isSelected) DeepBlue else LightGray,
                shape = RoundedCornerShape(6.dp)
            )
            .clickable(onClick = onClick)
            .padding(horizontal = 8.dp, vertical = 6.dp),
        contentAlignment = Alignment.Center
    ) {
        AppText(
            text = language.nativeName,
            style = MaterialTheme.typography.labelSmall,
            color = if (isSelected) DeepBlue else DarkGray,
            maxLines = 1
        )
    }
}

/**
 * Theme color selection item
 */
@Composable
private fun ThemeColorItem(
    theme: BackgroundTheme,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Column(
        modifier = Modifier
            .clip(RoundedCornerShape(6.dp))
            .clickable(onClick = onClick)
            .background(if (isSelected) Color(theme.topColor).copy(alpha = 0.3f) else Color.Transparent)
            .border(
                width = if (isSelected) 2.dp else 1.dp,
                color = if (isSelected) DeepBlue else LightGray,
                shape = RoundedCornerShape(6.dp)
            )
            .padding(6.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Color preview circle with gradient
        Box(
            modifier = Modifier
                .size(32.dp)
                .clip(CircleShape)
                .background(
                    brush = Brush.verticalGradient(
                        colors = listOf(
                            Color(theme.topColor),
                            Color(theme.bottomColor)
                        )
                    )
                )
        )
        Spacer(modifier = Modifier.height(2.dp))
        AppText(
            text = theme.displayName,
            style = MaterialTheme.typography.labelSmall,
            color = if (isSelected) DeepBlue else DarkGray
        )
    }
}

/**
 * Top area with app icon, title, and login avatar
 */
@Composable
private fun TopArea(
    onLogin: () -> Unit,
    backgroundTheme: BackgroundTheme,
    modifier: Modifier = Modifier
) {
    val strings = LocalStrings.current
    Row(
        modifier = modifier
            .padding(top = 16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // App Icon
        AppLogoIcon(
            size = 80.dp
        )
        
        Spacer(modifier = Modifier.width(12.dp))
        
        // Title
        AppText(
            text = strings.mainTitle,
            style = MaterialTheme.typography.displayLarge,
            color = Color(backgroundTheme.textColor)
        )
        
        Spacer(modifier = Modifier.weight(1f))
        
        // Login Avatar Button
        CircleIconButton(
            onClick = onLogin,
            size = 60.dp,
            backgroundColor = LightBlue
        ) {
            UserAvatarIcon(
                size = 30.dp,
                color = Color(backgroundTheme.textColor)
            )
        }
    }
}

/**
 * Main buttons area with Start Game, Level Select, and Leaderboard buttons
 */
@Composable
private fun MainButtonsArea(
    screenWidth: androidx.compose.ui.unit.Dp,
    onStartGame: () -> Unit,
    onLevelSelect: () -> Unit,
    onLeaderboard: () -> Unit,
    onHowToPlay: () -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = LocalStrings.current
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        // Start Game Button (40% width)
        PrimaryGameButton(
            text = strings.startGame,
            onClick = onStartGame,
            modifier = Modifier.width(screenWidth * 0.4f)
        )
        
        Spacer(modifier = Modifier.height(24.dp))
        
        // Level Select Button (35% width)
        SecondaryGameButton(
            text = strings.levelSelect,
            onClick = onLevelSelect,
            modifier = Modifier.width(screenWidth * 0.35f)
        )
        
        Spacer(modifier = Modifier.height(20.dp))
        
        // Leaderboard Button (30% width)
        TertiaryGameButton(
            text = strings.leaderboard,
            onClick = onLeaderboard,
            modifier = Modifier.width(screenWidth * 0.3f)
        )
        
        Spacer(modifier = Modifier.height(20.dp))
        
        // How to Play Button (30% width)
        TertiaryGameButton(
            text = strings.howToPlay,
            onClick = onHowToPlay,
            modifier = Modifier.width(screenWidth * 0.3f)
        )
    }
}

/**
 * Bottom area with social icons and action buttons
 */
@Composable
private fun BottomArea(
    onSettings: () -> Unit,
    onAboutUs: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .padding(bottom = 24.dp),
        verticalAlignment = Alignment.Bottom,
        horizontalArrangement = Arrangement.End
    ) {
        // Action Buttons (right side)
        ActionButtonsColumn(
            onSettings = onSettings,
            onAboutUs = onAboutUs
        )
    }
}

/**
 * Column of action buttons (Settings, About Us)
 */
@Composable
private fun ActionButtonsColumn(
    onSettings: () -> Unit,
    onAboutUs: () -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = LocalStrings.current
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.End,
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        BottomActionButton(
            text = strings.settings,
            onClick = onSettings,
            isPrimary = true
        )
        
        BottomActionButton(
            text = strings.aboutUs,
            onClick = onAboutUs,
            isPrimary = false
        )
    }
}

/**
 * How to Play dialog
 */
@Composable
private fun HowToPlayDialog(
    onDismiss: () -> Unit
) {
    val strings = LocalStrings.current
    
    Dialog(onDismissRequest = onDismiss) {
        Column(
            modifier = Modifier
                .widthIn(min = 280.dp, max = 400.dp)
                .clip(RoundedCornerShape(16.dp))
                .background(Color.White)
                .padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Title
            AppText(
                text = strings.howToPlay,
                style = MaterialTheme.typography.titleLarge,
                color = DeepBlue
            )
            
            Spacer(modifier = Modifier.height(20.dp))
            
            // Step 1
            HowToPlayStep(
                stepNumber = 1,
                description = strings.howToPlayStep1
            )
            
            Spacer(modifier = Modifier.height(12.dp))
            
            // Step 2
            HowToPlayStep(
                stepNumber = 2,
                description = strings.howToPlayStep2
            )
            
            Spacer(modifier = Modifier.height(12.dp))
            
            // Step 3
            HowToPlayStep(
                stepNumber = 3,
                description = strings.howToPlayStep3
            )
            
            Spacer(modifier = Modifier.height(16.dp))
            
            // Tip
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(8.dp))
                    .background(LightYellow)
                    .padding(12.dp)
            ) {
                AppText(
                    text = strings.howToPlayTip,
                    style = MaterialTheme.typography.bodyMedium,
                    color = DarkGray
                )
            }
            
            Spacer(modifier = Modifier.height(20.dp))
            
            // Close button
            SecondaryGameButton(
                text = strings.close,
                onClick = onDismiss,
                modifier = Modifier.width(120.dp)
            )
        }
    }
}

/**
 * Single step item in How to Play dialog
 */
@Composable
private fun HowToPlayStep(
    stepNumber: Int,
    description: String
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.Top
    ) {
        // Step number circle
        Box(
            modifier = Modifier
                .size(28.dp)
                .clip(CircleShape)
                .background(DeepBlue),
            contentAlignment = Alignment.Center
        ) {
            AppText(
                text = stepNumber.toString(),
                style = MaterialTheme.typography.bodyMedium,
                color = Color.White
            )
        }
        
        Spacer(modifier = Modifier.width(12.dp))
        
        // Description
        AppText(
            text = description,
            style = MaterialTheme.typography.bodyLarge,
            color = DarkGray,
            modifier = Modifier.weight(1f)
        )
    }
}
